import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { FaLinkedin } from "react-icons/fa";
import { User as UserIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { WorkerInfo } from "@/services/scrapingStatsApi";
import { resolveTierBadge } from "@/lib/tierBadge";
import { roleBadgeLabel } from "@/lib/roleBadge";
import { formatRelativeFr, getInitials } from "@/components/worker/workerFormatters";

// Identité worker partagée entre la page scraping-stats et le cockpit
// worker-tasks (table de répartition, tooltips de graphiques). Extrait tel quel
// de scraping-stats/page.tsx pour réutilisation (DRY).
//
// Les helpers de badge de tier (resolveTierBadge) et de rôle (roleBadgeLabel)
// vivent désormais dans @/lib/tierBadge et @/lib/roleBadge — partagés avec les
// vues users/abonnements/organisations. formatRelativeFr/getInitials vivent
// dans ./workerFormatters (non-composants, pour rester Fast-Refresh-friendly) —
// importez-les directement depuis là, pas depuis ce fichier.

// Handle du compte LinkedIn actuellement rattache au worker : icone de marque
// + "in/xxx" cliquable. Reutilise dans la cellule Worker ET dans le tooltip.
export function LinkedInHandle({ vanity }: { vanity: string }) {
  return (
    <a
      href={`https://www.linkedin.com/in/${vanity}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={`Ouvrir linkedin.com/in/${vanity}`}
      className="inline-flex min-w-0 items-center gap-1 text-muted-foreground transition-colors hover:text-[#0A66C2]"
    >
      <FaLinkedin className="h-3 w-3 shrink-0 text-[#0A66C2]" />
      <span className="truncate">in/{vanity}</span>
    </a>
  );
}

// Avatar identitaire du worker : initiales du recruteur (ou icone neutre si
// anonyme), avec une pastille de presence (vert = heartbeat frais, gris = hors
// ligne). Point d'ancrage visuel facon "member row".
export function WorkerAvatar({
  name,
  isAnonymous,
  isOnline,
}: {
  name?: string | null;
  isAnonymous?: boolean | null;
  isOnline: boolean;
}) {
  const initials = getInitials(name);
  const useIcon = Boolean(isAnonymous) || !initials;
  return (
    <span className="relative inline-flex shrink-0">
      <Avatar className={cn("h-9 w-9 border", !isOnline && "opacity-60")}>
        <AvatarFallback
          className={cn(
            "text-xs font-medium",
            useIcon
              ? "bg-muted text-muted-foreground"
              : "bg-gradient-to-br from-slate-200 to-slate-300 text-slate-700",
          )}
        >
          {useIcon ? <UserIcon className="h-4 w-4" /> : initials}
        </AvatarFallback>
      </Avatar>
      <span
        className={cn(
          "absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ring-background",
          isOnline ? "bg-emerald-500" : "bg-muted-foreground/40",
        )}
        title={isOnline ? "En ligne" : "Hors ligne"}
      />
    </span>
  );
}

export function WorkerCell({
  workerId,
  info,
}: {
  workerId: string;
  info: WorkerInfo | undefined;
}) {
  if (workerId === "unknown") {
    return <Badge variant="secondary">Inconnu</Badge>;
  }

  // Heartbeat frais (Redis, TTL ~90s) => en ligne. Si le nom vient de l'annuaire
  // Keycloak (user_name_source="directory"), c'est qu'aucun heartbeat n'est
  // frais => hors ligne.
  const isOnline = Boolean(info?.last_seen) && info?.user_name_source !== "directory";
  const vanity = info?.linkedin_public_identifier ?? null;
  const tierBadge = resolveTierBadge(info);
  const roleLabel = info?.member_role ? roleBadgeLabel(info.member_role) : null;
  // Profil RecrutAuto : la route attend le user_id Keycloak (jamais subscription_id).
  const profileHref = info?.user_id ? `/users/${info.user_id}` : null;

  // Ligne 1 : identite humaine la plus pertinente disponible.
  let name: ReactNode;
  if (info?.is_anonymous) {
    name = <span className="text-sm text-muted-foreground">Anonyme</span>;
  } else if (info?.user_name) {
    name = (
      <span className={cn("text-sm font-medium", !isOnline && "text-muted-foreground")}>
        {info.user_name}
      </span>
    );
  } else if (info?.subscription_id) {
    name = <span className="text-sm font-medium">Compte #{info.subscription_id}</span>;
  } else {
    name = <span className="text-sm text-muted-foreground">Hors compte</span>;
  }

  const lastSeenRel = formatRelativeFr(info?.last_seen);
  const browserLine = [
    info?.browser_name && info?.browser_version
      ? `${info.browser_name} ${info.browser_version}`
      : info?.browser_name,
    info?.os_name,
    info?.plugin_version ? `v${info.plugin_version}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const locationLine = [
    info?.ip ? `IP ${info.ip}` : null,
    [info?.location?.city, info?.location?.country].filter(Boolean).join(", ") || null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex cursor-help items-center gap-2.5">
          {profileHref ? (
            <Link to={profileHref} onClick={(e) => e.stopPropagation()} className="shrink-0">
              <WorkerAvatar name={info?.user_name} isAnonymous={info?.is_anonymous} isOnline={isOnline} />
            </Link>
          ) : (
            <WorkerAvatar name={info?.user_name} isAnonymous={info?.is_anonymous} isOnline={isOnline} />
          )}
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex items-center gap-1.5">
              {profileHref ? (
                <Link
                  to={profileHref}
                  onClick={(e) => e.stopPropagation()}
                  className="hover:underline"
                  title="Voir le compte utilisateur"
                >
                  {name}
                </Link>
              ) : (
                name
              )}
              {tierBadge && (
                <Badge
                  variant={tierBadge.variant}
                  className={cn(
                    "h-4 px-1 py-0 text-[9px] uppercase leading-none",
                    tierBadge.className,
                  )}
                >
                  {tierBadge.label}
                </Badge>
              )}
            </div>
            {roleLabel && (
              <div className="text-[10px] leading-none text-muted-foreground">{roleLabel}</div>
            )}
            <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
              {vanity ? (
                <LinkedInHandle vanity={vanity} />
              ) : (
                <span className="inline-flex items-center gap-1 italic">
                  <FaLinkedin className="h-3 w-3 shrink-0 opacity-40" />
                  compte inconnu
                </span>
              )}
              {info?.plugin_version && <span className="shrink-0">· v{info.plugin_version}</span>}
            </div>
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm space-y-1 text-xs">
        <div className="font-mono break-all">{workerId}</div>
        {info?.subscription_id != null && (
          <div className="text-muted-foreground">
            Sub #{info.subscription_id}
            {info.user_id ? (
              <>
                {" · "}
                <span className="font-mono">{info.user_id.substring(0, 8)}…</span>
              </>
            ) : null}
          </div>
        )}
        {(info?.organization_name || info?.organization_tier || roleLabel) && (
          <div className="text-muted-foreground">
            {info?.organization_name ?? "Organisation"}
            {info?.organization_tier ? ` · ${info.organization_tier}` : ""}
            {roleLabel ? ` · ${roleLabel}` : ""}
          </div>
        )}
        {browserLine && <div>{browserLine}</div>}
        {lastSeenRel ? (
          <div>Dernière activité : {lastSeenRel}</div>
        ) : (
          <div className="italic text-muted-foreground">Plugin hors ligne</div>
        )}
        {locationLine && <div className="text-muted-foreground">{locationLine}</div>}
        {vanity && (
          <div className="pt-0.5">
            <LinkedInHandle vanity={vanity} />
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
